import styles from './TestForm.module.scss'

import {
  Form,
  FormItem,
  InputPercent,
  RangePicker,
  InputAmount,
  Box,
  Button,
} from 'common/components'
import {
  formatDate,
  getInitialForecast,
  fixNumber,
  totalNumberFromArray,
} from 'common/helpers'
import {FIELD_RULES} from 'common/constants'

interface TestFormProps {
  isMutating: boolean
  getTestData: any
}

export default function TestForm({isMutating, getTestData}: TestFormProps) {
  const handleFinish = ({
    dates,
    loanValue,
    initialDrawdown,
    ...values
  }: any) => {
    const newValues = {
      ...values,
      loanValue,
      forecastItems: getForecast(loanValue, dates, initialDrawdown),
      openDate: formatDate(dates[0], 'server'),
      maturityDate: formatDate(dates[1], 'server'),
    }
    getTestData(newValues)
  }

  return (
    <Form onFinish={handleFinish}>
      <div className={styles.fieldset}>
        <FormItem
          name="dates"
          label="Loan open & maturity dates"
          rules={FIELD_RULES}
        >
          <RangePicker />
        </FormItem>
        <FormItem
          name="loanValue"
          label="Approved loan amount"
          rules={FIELD_RULES}
        >
          <InputAmount />
        </FormItem>
        <FormItem
          name="initialDrawdown"
          label="Initial drawdown amount"
          rules={FIELD_RULES}
        >
          <InputAmount min={0} />
        </FormItem>
      </div>
      <div className={styles.fieldset}>
        <FormItem
          name="interest"
          label="Loan interest"
          rules={percentFieldRules}
        >
          <InputPercent />
        </FormItem>
        <FormItem
          name="lineFee"
          label="Line fee, monthly"
          rules={percentFieldRules}
        >
          <InputPercent />
        </FormItem>
        <FormItem
          name="establishFee"
          label="Establish. fee"
          rules={percentFieldRules}
        >
          <InputPercent />
        </FormItem>
        <FormItem
          name="applicationFeeAmount"
          label="Application fee"
          rules={FIELD_RULES}
        >
          <InputAmount />
        </FormItem>
        <FormItem
          name="brokerageFeeAmount"
          label="Broker fee"
          rules={FIELD_RULES}
        >
          <InputAmount min={0} />
        </FormItem>
        <FormItem name="legalFeeAmount" label="Legal fee" rules={FIELD_RULES}>
          <InputAmount />
        </FormItem>
        <FormItem
          name="securityValue"
          label="Security value"
          rules={FIELD_RULES}
        >
          <InputAmount />
        </FormItem>
      </div>
      <Box align="center">
        <Button
          className={styles.submit}
          htmlType="submit"
          loading={isMutating}
        >
          Get results
        </Button>
      </Box>
    </Form>
  )
}

const getForecast = (
  loanValue: number,
  dates: Date[],
  initialDrawdown: number
) => {
  const initialForecast = getInitialForecast(loanValue, dates[0], dates[1])
  const availableForecastAmount = loanValue - initialDrawdown
  const forecastItemAmount = fixNumber(
    availableForecastAmount / (initialForecast.length - 1)
  )
  const forecast = initialForecast.map((item, index) => ({
    type: 'drawdown',
    initialDrawdown: index === 0,
    date: formatDate(item.date, 'server'),
    amount:
      index === 0
        ? initialDrawdown
        : availableForecastAmount > 0
        ? forecastItemAmount
        : 0,
  }))
  if (availableForecastAmount <= 0) return forecast
  const forecastTotalAmount = totalNumberFromArray(forecast, 'amount')
  if (forecastTotalAmount !== loanValue) {
    const lastItem = forecast.at(-1)
    forecast.at(-1).amount = fixNumber(
      lastItem.amount - (forecastTotalAmount - loanValue)
    )
  }
  return forecast
}

const percentFieldRules = [
  ...FIELD_RULES,
  () => ({
    validator(_: any, value: any) {
      if (value) {
        return Promise.resolve()
      }
      return Promise.reject()
    },
  }),
]
